import React, { useEffect, useState } from "react";
import { Cpu, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Field } from "@/pages/crm/LeadForm";

const blank = {
  lead_id: "", customer_name: "", company: "", project_name: "", service_type: "Orthomosaic",
  engineer_id: "", images: 0, size_gb: 0, area_sqkm: 0, sensor: "", complexity: "Normal",
  expected_hours: 0, due_date: "", input_notes: "", output_format: "", priority: "Normal",
};

export const ProcessingForm = ({ open, job, meta, onOpenChange, onSaved }) => {
  const editing = !!job;
  const [form, setForm] = useState(blank);
  const [contacts, setContacts] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(editing ? { ...blank, ...job } : blank);
    api.get("/demos/contacts").then((r) => setContacts(r.data)).catch(() => {});
  }, [open, job, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const bench = (meta?.benchmarks || {})[form.service_type];

  const pickContact = (id) => {
    const c = contacts.find((x) => x.id === id);
    if (!c) return;
    setForm((f) => ({ ...f, lead_id: c.id, customer_name: c.customer_name, company: c.company || "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) return toast.error("Choose the customer.");
    if (!form.project_name.trim()) return toast.error("Give the project a name.");
    setBusy(true);
    const payload = {
      ...form, images: Number(form.images || 0), size_gb: Number(form.size_gb || 0),
      area_sqkm: Number(form.area_sqkm || 0), expected_hours: Number(form.expected_hours || 0),
    };
    try {
      if (editing) {
        await api.patch(`/processing/${job.id}`, payload);
        toast.success("Job updated");
        onSaved?.(job.id);
      } else {
        const { data } = await api.post("/processing", payload);
        toast.success(`Job ${data.number} created`);
        onSaved?.(data.id);
      }
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto scroll-slim">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {editing ? `Edit ${job.number}` : "New processing job"}
          </DialogTitle>
          <DialogDescription>
            The data size and complexity decide the expected hours, so efficiency stays fair.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Customer" required>
              <Select value={form.lead_id} onValueChange={pickContact}>
                <SelectTrigger data-testid="pf-contact">
                  <SelectValue placeholder={form.customer_name || "Select a customer or lead"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Customers</SelectLabel>
                    {contacts.filter((c) => c.kind === "customer").map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.customer_name}{c.company ? ` \u00b7 ${c.company}` : ""}</SelectItem>
                    ))}
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel>CRM leads</SelectLabel>
                    {contacts.filter((c) => c.kind === "lead").map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.customer_name}{c.company ? ` \u00b7 ${c.company}` : ""}</SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Project name" required>
              <Input value={form.project_name} data-testid="pf-project"
                onChange={(e) => set("project_name", e.target.value)} placeholder="Sanand plant survey - phase 2" />
            </Field>
            <Field label="Service" required>
              <Select value={form.service_type} onValueChange={(v) => set("service_type", v)}>
                <SelectTrigger data-testid="pf-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta?.types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Engineer">
              <Select value={form.engineer_id} onValueChange={(v) => set("engineer_id", v)}>
                <SelectTrigger data-testid="pf-engineer"><SelectValue placeholder="Assign to me" /></SelectTrigger>
                <SelectContent>
                  {(meta?.engineers || []).map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid gap-4 rounded-xl border border-border bg-muted/30 p-3 sm:grid-cols-4">
            <Field label="Images">
              <Input type="number" min="0" value={form.images} data-testid="pf-images"
                onChange={(e) => set("images", e.target.value)} />
            </Field>
            <Field label="Data size (GB)">
              <Input type="number" min="0" step="0.1" value={form.size_gb} data-testid="pf-gb"
                onChange={(e) => set("size_gb", e.target.value)} />
            </Field>
            <Field label="Area (sq km)">
              <Input type="number" min="0" step="0.01" value={form.area_sqkm} data-testid="pf-area"
                onChange={(e) => set("area_sqkm", e.target.value)} />
            </Field>
            <Field label="Complexity">
              <Select value={form.complexity} onValueChange={(v) => set("complexity", v)}>
                <SelectTrigger data-testid="pf-complexity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta?.complexity || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Sensor / camera">
              <Input value={form.sensor} data-testid="pf-sensor" onChange={(e) => set("sensor", e.target.value)} />
            </Field>
            <Field label="Expected hours" hint={bench
              ? `Leave 0 to use our benchmark of ${bench.hours_per_unit} h per unit (${bench.source})`
              : "Leave 0 to use the learned benchmark"}>
              <Input type="number" min="0" step="0.5" value={form.expected_hours} data-testid="pf-expected"
                onChange={(e) => set("expected_hours", e.target.value)} />
            </Field>
            <Field label="Deliver by">
              <Input type="date" value={form.due_date} data-testid="pf-due"
                onChange={(e) => set("due_date", e.target.value)} />
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Output format">
              <Input value={form.output_format} data-testid="pf-output"
                onChange={(e) => set("output_format", e.target.value)} placeholder="GeoTIFF + LAS + PDF report" />
            </Field>
            <Field label="Priority">
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger data-testid="pf-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Low", "Normal", "High", "Urgent"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Notes about the input data">
            <Textarea rows={2} value={form.input_notes} data-testid="pf-notes"
              onChange={(e) => set("input_notes", e.target.value)}
              placeholder="RTK logs missing for flight 3, GCP file shared separately." />
          </Field>

          <DialogFooter>
            <Button type="button" variant="outline" data-testid="pf-cancel" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="pf-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Cpu className="h-4 w-4" />}
              {editing ? "Save" : "Create job"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
